import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Turning } from '@/ui/core/motion'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import type { ReactNode } from 'react'
import { View } from 'react-native'

/** A small caps title over a blush card of rows, the title on the rows' own left edge. */
export const RowGroup = ({
	title,
	trailing,
	children,
}: { title?: string; trailing?: ReactNode; children: ReactNode }) => (
	<VStack gap={8}>
		{!!title && (
			<View className="min-h-[24px] flex-row items-center justify-between px-4">
				<Text size="body3" tone="soft" className="tracking-[0.08em]">
					{title}
				</Text>
				{trailing}
			</View>
		)}
		<View className="rounded-note bg-blush px-4">{children}</View>
	</VStack>
)

/**
 * Where a row stands. `due` wants the member to do something and wears the accent; `wait` is
 * merely not settled yet — a payment on its way — and stays neutral, so coral keeps meaning
 * "this one needs you".
 */
export type RowStatus = 'done' | 'due' | 'wait' | 'alert' | 'off'

/**
 * The brand's marks are black or white, and the asterisk is its own sign for a thing to do —
 * the home to-do already wears it. Settled and pending share one soft ink, so the row's colour
 * budget goes to the coral dot beside the chevron; the pending mark tells itself apart by turning.
 */
const ROW_MARK: Record<RowStatus, { glyph: GlyphName; color: string }> = {
	done: { glyph: 'check', color: colors.softInk },
	due: { glyph: 'asterisk', color: colors.softInk },
	wait: { glyph: 'asterisk', color: colors.softInk },
	alert: { glyph: 'x', color: colors.danger },
	off: { glyph: 'check', color: colors.muted },
}

const STATUS_WORD: Record<RowStatus, string> = {
	done: 'Paid',
	due: 'Due',
	wait: 'Due',
	alert: 'Needs attention',
	off: 'Refunded',
}

/**
 * A row that simply wants doing says so with a dot, the same one the home to-do wears. Rows
 * that are settled carry nothing — their date under the title is enough.
 */
export const Dot = ({ tone = 'due' }: { tone?: 'due' | 'alert' }) => (
	<View
		className="h-2 w-2 rounded-full"
		style={{ backgroundColor: tone === 'alert' ? colors.danger : colors.accent }}
	/>
)

/**
 * When something is actually wrong, a dot is not enough and the row says the word. Solid, so it
 * is the only filled shape in the row and cannot be mistaken for the white disc on the left.
 */
export const Tag = ({
	label,
	tone = 'due',
}: { label: string; tone?: 'due' | 'alert' }) => (
	<View
		className="flex-row items-center self-center rounded-full px-2.5"
		style={{
			height: 19,
			backgroundColor: tone === 'alert' ? colors.danger : colors.accent,
		}}
	>
		{/* The same small caps the section titles use, so the two read as one voice. */}
		<Text size="body3" className="tracking-[0.08em]" style={{ color: colors.white }}>
			{label}
		</Text>
	</View>
)

/** The mark in a row's disc. On anything not yet settled it turns. */
const RowMark = ({ status }: { status: RowStatus }) => {
	const glyph = (
		<Glyph name={ROW_MARK[status].glyph} size={18} color={ROW_MARK[status].color} />
	)
	return status === 'wait' || status === 'due' ? <Turning>{glyph}</Turning> : glyph
}

const PILL_MARK: Record<RowStatus, GlyphName> = {
	done: 'check',
	due: 'clock',
	wait: 'clock',
	alert: 'x',
	off: 'x',
}

/**
 * The neutral pill's ground: the step between `surface` and `line`.
 *
 * Neither token works here. `surface` is the near-white a disabled input is filled with, and at
 * #F9F9F9 on a white card it sits four points off the card it stands on — a pill you can only
 * find by reading its label. `line` is the solid grey a switch track is drawn in, and on
 * something this small it reads as a chip of hardware. This is between them, in the same warm
 * neutral family as both: a resting state gets a ground without announcing itself as a control.
 */
const PILL_GROUND = '#EEECEC'

/**
 * A status as a small pill: mint for paid, grey for due, blush when it needs attention — unless
 * `neutral` takes the colour out of it. See both props.
 */
export const StatusPill = ({
	status,
	label,
	glyph,
	neutral = false,
	onGround = false,
}: {
	status: RowStatus
	label?: string
	/**
	 * The mark, where the status's own is not the right one: the whitening kit's two "ready"
	 * states wear a star, because neither is waiting nor done and a clock on either would say
	 * it was.
	 */
	glyph?: GlyphName
	/**
	 * One ground and an ink mark, whatever the state says.
	 *
	 * For a pill that is the only badge in its corner. A reader meets one of those a state at a
	 * time, days or weeks apart, so mint-for-done against grey-for-waiting never reads as a
	 * scale — it reads as two different kinds of object standing in one place. Where a pill is
	 * one of several down a list, the tints are a scale again and this stays off.
	 *
	 * `alert` keeps its red regardless: "Needs attention" is the one label whose colour is
	 * information the word cannot carry on its own.
	 */
	neutral?: boolean
	/**
	 * This pill is standing on the page's pink, or on a photograph, rather than on a card, so its
	 * resting ground is white.
	 *
	 * A neutral pill needs to be a shade off whatever is behind it, and almost every one of these
	 * is behind a white card, so the default is the grey. A group's title row is not on a card —
	 * it is on the ground, above one — and a grey pill there is a second neutral on a pink page
	 * for no reason. White is what every other object on that page does.
	 */
	onGround?: boolean
}) => {
	/*
	 * One pill, one set of tints, wherever it is drawn.
	 *
	 * A `warm` variant existed for a while — blush ground, coral mark — on the theory that the
	 * neutral was built for a billing row, where the row's own furniture holds it, and that a
	 * pill standing alone on a white tile needed a ground of its own. On the tile it read as a
	 * second kind of pill, and a member who saw the membership's state on the billing screen
	 * and the whitening kit's on Home had no way to know the two meant the same thing. The
	 * neutral holds on white perfectly well; what it was missing was nothing.
	 */
	const tint =
		status === 'alert'
			? { bg: colors.blush, fg: colors.danger, text: colors.danger }
			: status === 'done' && !neutral
			  ? { bg: colors.mint, fg: colors.leaf, text: colors.ink }
			  : { bg: onGround ? colors.white : PILL_GROUND, fg: colors.ink, text: colors.ink }
	return (
		<View
			className="flex-row items-center self-start rounded-full px-2"
			style={{ height: 22, gap: 5, backgroundColor: tint.bg }}
		>
			<Glyph name={glyph ?? PILL_MARK[status]} size={12} color={tint.fg} />
			<Text size="body2" weight="bold" style={{ color: tint.text }}>
				{label ?? STATUS_WORD[status]}
			</Text>
		</View>
	)
}

/**
 * One row in a billing group: a glyph on a white disc — or a numeral, where a list is ordered —
 * a title, a line beneath, and on the right an amount or whatever the row needs. A status badge sits on the disc's corner. A
 * hairline between rows, from the text's left edge, none under the last.
 */
export const Row = ({
	status,
	glyph,
	title,
	line,
	amount,
	amountTone = 'ink',
	trailing,
	lineNode,
	tag,
	tagOnTitle = false,
	lineSmall = false,
	mark,
	onPress,
	last = false,
	wrap = false,
}: {
	/** Omitted where the row carries an action's glyph rather than a thing's state. */
	status?: RowStatus
	/**
	 * Stands in the disc in place of a status mark. The statuses are billing's vocabulary — paid,
	 * due, refunded — and a row that is a way out of a visit is not in any of those states.
	 */
	glyph?: GlyphName
	title: string
	line?: string
	amount?: string
	amountTone?: 'ink' | 'soft' | 'danger'
	trailing?: ReactNode
	/** Rendered under the title in place of `line`, for a state that wants its own shape. */
	lineNode?: ReactNode
	/** A tag before the amount and the chevron, for a row that needs something. */
	tag?: ReactNode
	/**
	 * The tag rides the title instead of standing between the words and the chevron.
	 *
	 * A pill and a chevron take about 130 of a row's 366 points, which leaves a second line
	 * nowhere to go: a date broke as "Sep 29, 2026 ·" over "Tribeca", which reads as a fault
	 * rather than as a long line. The pill belongs to the thing rather than to its description,
	 * so up there it costs the title — which is short — instead of the line, which is not.
	 */
	tagOnTitle?: boolean
	/**
	 * The second line one size down, for a row whose line has to share its width.
	 *
	 * Billing's rows hold an amount and nothing else beside the words, so they keep the larger
	 * line. Twelve is the size every subtitle in a sheet is already set at.
	 */
	lineSmall?: boolean
	/** Stands in the disc in place of the status mark: the membership sheet's padded numeral. */
	mark?: string
	onPress?: () => void
	last?: boolean
	/** Let a long title and line run to two lines. Billing rows stay clamped to one. */
	wrap?: boolean
}) => (
	<Pressable
		onPress={onPress}
		disabled={!onPress}
		accessibilityRole={onPress ? 'button' : undefined}
		accessible={!!onPress}
		accessibilityLabel={[title, line, amount].filter(Boolean).join('. ')}
		className="min-h-[68px] flex-row items-center py-3"
		style={{ gap: 14 }}
	>
		{/* The hairline starts where the words do, past the disc. */}
		{!last && (
			<View
				className="absolute right-0 bottom-0 h-px"
				style={{ left: 48, backgroundColor: colors.divider }}
			/>
		)}
		<View className="h-10 w-10 items-center justify-center rounded-full bg-white">
			{mark ? (
				<Text className="font-display text-[17px] leading-[20px] tracking-[-0.02em]">
					{mark}
				</Text>
			) : glyph ? (
				<Glyph name={glyph} size={18} color={colors.softInk} />
			) : status ? (
				<RowMark status={status} />
			) : null}
		</View>
		<VStack gap={2} className="flex-1">
			{tagOnTitle && tag ? (
				<HStack gap={10} className="items-center justify-between">
					<Text size="body0" weight="bold" numberOfLines={1} className="flex-1">
						{title}
					</Text>
					{tag}
				</HStack>
			) : (
				<Text size="body0" weight="bold" numberOfLines={wrap ? 2 : 1}>
					{title}
				</Text>
			)}
			{lineNode ??
				(!!line && (
					<Text
						size={lineSmall ? 'body2' : 'body1'}
						tone="soft"
						numberOfLines={wrap ? 2 : 1}
					>
						{line}
					</Text>
				))}
		</VStack>
		{!tagOnTitle && tag}
		{!!amount && (
			<Text size="body0" weight="bold" tone={amountTone} className="pl-2">
				{amount}
			</Text>
		)}
		{trailing ?? (onPress ? <Glyph name="chevron" size={14} color={colors.ink} /> : null)}
	</Pressable>
)

/** The last row of a trimmed list: "See All" with the same chevron as the rows above it. */
export const SeeAllRow = ({ onPress }: { onPress: () => void }) => (
	<Pressable
		onPress={onPress}
		accessibilityRole="button"
		accessibilityLabel="See all"
		className="min-h-[56px] flex-row items-center justify-between py-3"
		style={{ paddingLeft: 50 }}
	>
		<Text size="body0" weight="bold">
			See All
		</Text>
		<Glyph name="chevron" size={14} color={colors.ink} />
	</Pressable>
)
